import React, { useEffect, useState, useCallback } from "react";
import API_BASE from "../config";

const PROMPTS_URL = `${API_BASE}/api/super-admin/prompts`;

function authHeaders() {
  return {
    "Content-Type": "application/json",
    Authorization: "Bearer " + (localStorage.getItem("idToken") || ""),
  };
}

function fmtDate(v) {
  if (!v) return "";
  try {
    return new Date(v).toLocaleString();
  } catch {
    return String(v);
  }
}

const styles = {
  layout: { display: "flex", gap: 16, alignItems: "flex-start" },
  sidebar: { width: 300, flexShrink: 0, borderRight: "1px solid #e2e8f0", paddingRight: 12, maxHeight: "75vh", overflow: "auto" },
  catLabel: { fontSize: 12, fontWeight: 700, color: "#6366f1", textTransform: "uppercase", margin: "12px 0 4px" },
  item: (active) => ({
    padding: "7px 9px",
    borderRadius: 6,
    cursor: "pointer",
    fontSize: 13,
    background: active ? "#eef2ff" : "transparent",
    fontWeight: active ? 600 : 400,
    color: "#1e293b",
  }),
  main: { flex: 1, minWidth: 0 },
  textarea: { width: "100%", minHeight: 380, fontFamily: "monospace", fontSize: 13, padding: 10, border: "1px solid #cbd5e1", borderRadius: 8, boxSizing: "border-box", whiteSpace: "pre" },
  input: { width: "100%", padding: 8, border: "1px solid #cbd5e1", borderRadius: 6, boxSizing: "border-box", marginTop: 8 },
  btn: { padding: "7px 14px", border: "1px solid #cbd5e1", borderRadius: 6, background: "#fff", cursor: "pointer", fontSize: 13 },
  btnPrimary: { padding: "7px 14px", border: "none", borderRadius: 6, background: "#2563eb", color: "#fff", cursor: "pointer", fontSize: 13 },
  th: { textAlign: "left", padding: 8, borderBottom: "2px solid #e2e8f0", fontSize: 12, color: "#475569" },
  td: { padding: 8, borderBottom: "1px solid #eef2f7", verticalAlign: "top", fontSize: 12 },
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
  modal: { background: "#fff", borderRadius: 10, padding: 20, width: "85%", maxWidth: 1000, maxHeight: "85vh", overflow: "auto" },
  pre: { whiteSpace: "pre-wrap", wordBreak: "break-word", background: "#f8fafc", padding: 8, borderRadius: 6, fontFamily: "monospace", fontSize: 12, maxHeight: 200, overflow: "auto" },
};

export default function Prompts() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedKey, setSelectedKey] = useState(null);
  const [current, setCurrent] = useState(null); // {prompt_key,name,description,content,...}
  const [draft, setDraft] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState("");
  const [historyRows, setHistoryRows] = useState(null); // null=closed
  const emptyNewP = { prompt_key: "", name: "", category: "", description: "", content: "", reason: "" };
  const [showAdd, setShowAdd] = useState(false);
  const [newP, setNewP] = useState(emptyNewP);
  const [addBanner, setAddBanner] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteBanner, setDeleteBanner] = useState("");

  const loadList = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(PROMPTS_URL, { headers: authHeaders(), credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setList(data.prompts || []);
    } catch (e) {
      setError("Failed to load prompts: " + e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  async function selectPrompt(key) {
    setSelectedKey(key);
    setCurrent(null);
    setBanner("");
    setReason("");
    try {
      const res = await fetch(`${PROMPTS_URL}/${encodeURIComponent(key)}`, {
        headers: authHeaders(),
        credentials: "include",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setCurrent(data.prompt);
      setDraft(data.prompt.content == null ? "" : data.prompt.content);
    } catch (e) {
      setBanner("Failed to load prompt: " + e.message);
    }
  }

  async function save() {
    if (!reason.trim()) {
      setBanner("A reason for the change is required.");
      return;
    }
    setSaving(true);
    setBanner("");
    try {
      const res = await fetch(`${PROMPTS_URL}/${encodeURIComponent(selectedKey)}`, {
        method: "PUT",
        headers: authHeaders(),
        credentials: "include",
        body: JSON.stringify({ content: draft, reason: reason.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setBanner("Saved. Changes go live in the app within ~60s.");
      setReason("");
      await selectPrompt(selectedKey);
      await loadList();
    } catch (e) {
      setBanner("Save failed: " + e.message);
    } finally {
      setSaving(false);
    }
  }

  async function openHistory() {
    setHistoryRows([]);
    try {
      const res = await fetch(`${PROMPTS_URL}/${encodeURIComponent(selectedKey)}/audit`, {
        headers: authHeaders(),
        credentials: "include",
      });
      const data = await res.json();
      setHistoryRows(data.audit || []);
    } catch {
      setHistoryRows([]);
    }
  }

  async function createPrompt() {
    if (!newP.prompt_key.trim() || !newP.name.trim() || !newP.reason.trim()) {
      setAddBanner("Key, name and reason are required.");
      return;
    }
    try {
      const res = await fetch(PROMPTS_URL, {
        method: "POST",
        headers: authHeaders(),
        credentials: "include",
        body: JSON.stringify(newP),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setShowAdd(false);
      const createdKey = data.prompt_key;
      setNewP(emptyNewP);
      await loadList();
      await selectPrompt(createdKey);
    } catch (e) {
      setAddBanner("Create failed: " + e.message);
    }
  }

  async function deletePrompt() {
    if (!deleteReason.trim()) { setDeleteBanner("A reason is required."); return; }
    try {
      const res = await fetch(`${PROMPTS_URL}/${encodeURIComponent(selectedKey)}`, {
        method: "DELETE",
        headers: authHeaders(),
        credentials: "include",
        body: JSON.stringify({ reason: deleteReason.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setDeleteOpen(false);
      setDeleteReason("");
      setSelectedKey(null);
      setCurrent(null);
      setBanner("Prompt deleted.");
      await loadList();
    } catch (e) {
      setDeleteBanner("Delete failed: " + e.message);
    }
  }

  const addLbl = { display: "block", fontSize: 12, color: "#475569", margin: "8px 0 2px" };

  const groups = list.reduce((acc, p) => {
    (acc[p.category] = acc[p.category] || []).push(p);
    return acc;
  }, {});
  const categories = Object.keys(groups).sort();
  const dirty = current && draft !== (current.content == null ? "" : current.content);

  return (
    <div>
      <h1>Prompts</h1>
      <p style={{ color: "#64748b" }}>
        Edit the system prompts used across the app. Saved changes are stored in the database with an
        audit trail and take effect within ~60 seconds (running jobs keep the prompt they started with).
      </p>

      {error && <p style={{ color: "#b91c1c" }}>{error}</p>}

      <div style={{ marginBottom: 10 }}>
        <button style={styles.btnPrimary} onClick={() => { setNewP(emptyNewP); setAddBanner(""); setShowAdd(true); }}>
          ➕ New prompt
        </button>
      </div>

      <div style={styles.layout}>
        <div style={styles.sidebar}>
          {loading && <p>Loading…</p>}
          {categories.map((cat) => (
            <div key={cat}>
              <div style={styles.catLabel}>{cat}</div>
              {groups[cat].map((p) => (
                <div
                  key={p.prompt_key}
                  style={styles.item(selectedKey === p.prompt_key)}
                  onClick={() => selectPrompt(p.prompt_key)}
                >
                  {p.name}
                </div>
              ))}
            </div>
          ))}
        </div>

        <div style={styles.main}>
          {banner && (
            <div style={{ padding: 10, marginBottom: 12, background: "#ecfeff", border: "1px solid #a5f3fc", borderRadius: 8 }}>
              {banner}
            </div>
          )}

          {!selectedKey && <p style={{ color: "#94a3b8" }}>Select a prompt on the left to view and edit it.</p>}

          {selectedKey && current && (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                <h3 style={{ margin: "0 0 4px" }}>{current.name}</h3>
                <div style={{ display: "flex", gap: 6 }}>
                  <button style={styles.btn} onClick={openHistory}>History</button>
                  <button
                    style={{ ...styles.btn, color: "#b91c1c" }}
                    onClick={() => { setDeleteOpen(true); setDeleteReason(""); setDeleteBanner(""); }}
                  >
                    Delete
                  </button>
                </div>
              </div>
              {current.description && <div style={{ color: "#94a3b8", fontSize: 13, marginBottom: 8 }}>{current.description}</div>}
              {current.updated_by_email && (
                <div style={{ color: "#cbd5e1", fontSize: 12, marginBottom: 8 }}>
                  last changed by {current.updated_by_email} · {fmtDate(current.updated_at)}
                </div>
              )}

              <textarea style={styles.textarea} value={draft} onChange={(e) => setDraft(e.target.value)} spellCheck={false} />
              <input
                style={styles.input}
                placeholder="Reason for this change (required)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center" }}>
                <button style={styles.btnPrimary} disabled={saving || !dirty} onClick={save}>
                  {saving ? "Saving…" : "Save changes"}
                </button>
                <button
                  style={styles.btn}
                  disabled={saving || !dirty}
                  onClick={() => { setDraft(current.content == null ? "" : current.content); setReason(""); }}
                >
                  Revert
                </button>
                {dirty && <span style={{ color: "#b45309", fontSize: 12 }}>Unsaved changes</span>}
              </div>
            </>
          )}
        </div>
      </div>

      {showAdd && (
        <div style={styles.modalOverlay} onClick={() => setShowAdd(false)}>
          <div style={{ ...styles.modal, maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>New prompt</h3>
            {addBanner && <div style={{ color: "#b91c1c", marginBottom: 8 }}>{addBanner}</div>}
            <label style={addLbl}>Key (letters, numbers, underscores)</label>
            <input style={styles.input} value={newP.prompt_key} onChange={(e) => setNewP({ ...newP, prompt_key: e.target.value })} />
            <label style={addLbl}>Name</label>
            <input style={styles.input} value={newP.name} onChange={(e) => setNewP({ ...newP, name: e.target.value })} />
            <label style={addLbl}>Category</label>
            <input style={styles.input} list="prompt-cats" value={newP.category} onChange={(e) => setNewP({ ...newP, category: e.target.value })} />
            <datalist id="prompt-cats">{categories.map((c) => <option key={c} value={c} />)}</datalist>
            <label style={addLbl}>Description</label>
            <input style={styles.input} value={newP.description} onChange={(e) => setNewP({ ...newP, description: e.target.value })} />
            <label style={addLbl}>Content</label>
            <textarea style={{ ...styles.textarea, minHeight: 220 }} value={newP.content} onChange={(e) => setNewP({ ...newP, content: e.target.value })} spellCheck={false} />
            <label style={addLbl}>Reason (required)</label>
            <input style={styles.input} value={newP.reason} onChange={(e) => setNewP({ ...newP, reason: e.target.value })} />
            <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
              <button style={styles.btnPrimary} onClick={createPrompt}>Create</button>
              <button style={styles.btn} onClick={() => setShowAdd(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {deleteOpen && current && (
        <div style={styles.modalOverlay} onClick={() => setDeleteOpen(false)}>
          <div style={{ ...styles.modal, maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Delete prompt</h3>
            <p style={{ fontSize: 14 }}>
              Delete <b>{current.name}</b> (<code>{selectedKey}</code>)? This is recorded in the audit
              log. If the app still uses this prompt and it has no bundled fallback file, that feature
              may break until it is recreated.
            </p>
            {deleteBanner && <div style={{ color: "#b91c1c", marginBottom: 8 }}>{deleteBanner}</div>}
            <input style={styles.input} placeholder="Reason (required)" value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)} />
            <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
              <button style={{ ...styles.btnPrimary, background: "#b91c1c" }} onClick={deletePrompt}>Delete</button>
              <button style={styles.btn} onClick={() => setDeleteOpen(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {historyRows !== null && (
        <div style={styles.modalOverlay} onClick={() => setHistoryRows(null)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3 style={{ margin: 0 }}>History — {current && current.name}</h3>
              <button style={styles.btn} onClick={() => setHistoryRows(null)}>Close</button>
            </div>
            {historyRows.length === 0 && <p>No changes recorded for this prompt.</p>}
            {historyRows.map((a) => (
              <div key={a.id} style={{ borderTop: "1px solid #e2e8f0", paddingTop: 10, marginTop: 10 }}>
                <div style={{ fontSize: 12, color: "#475569" }}>
                  <b>{fmtDate(a.changed_at)}</b> · {a.changed_by_email} · {a.source_ip}
                </div>
                <div style={{ fontSize: 13, margin: "4px 0" }}>Reason: {a.reason}</div>
                <div style={{ display: "flex", gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, color: "#b91c1c" }}>Before</div>
                    <div style={styles.pre}>{a.old_content}</div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, color: "#15803d" }}>After</div>
                    <div style={styles.pre}>{a.new_content}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
